import React, { useEffect, useMemo, useState } from 'react'
import { Combobox, Modal } from '@enamelai/design-system'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { fetchAllPatients } from '@store/actions/patientmanagement.action'
import type { Patient } from '@dataTypes/PatientInfo.types'
import type { PendingFormEntry } from '@pages/FormBuilder/assignedForms.types'
import { createPatientFormsOtp } from '@services/api/otp.api'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'
import AssignFormsModal from './AssignFormsModal.component'
import PatientOTPModal from '../PendingFormsTable/PatientOTPModal.component'
import './SendPatientFormsModal.css'

interface Props {
  show: boolean
  onHide: () => void
  patient?: Patient | null
  onFormsAssigned?: (entry: PendingFormEntry) => void
}

const getPatientLabel = (patient: Patient): string => {
  const name = `${patient.first_name} ${patient.last_name}`.trim()
  return patient.public_id ? `${name} (${patient.public_id})` : name
}

const SendPatientFormsModal: React.FC<Props> = ({
  show,
  onHide,
  patient,
  onFormsAssigned,
}) => {
  const dispatch = useAppDispatch()
  const patients = useAppSelector((state) => state.patient.data)
  const { showToast } = useNotificationToast()
  const [searchText, setSearchText] = useState('')
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [otp, setOtp] = useState<{ patient: Patient; code: string } | null>(
    null,
  )
  const [regenerating, setRegenerating] = useState(false)
  const target = patient ?? selectedPatient

  const patientByLabel = useMemo(
    () =>
      new Map(
        (patients ?? []).map((item) => [getPatientLabel(item), item] as const),
      ),
    [patients],
  )
  const patientOptions = useMemo(
    () =>
      Array.from(patientByLabel.keys()).map((label) => ({
        value: label,
        label,
      })),
    [patientByLabel],
  )

  useEffect(() => {
    if (!show) {
      setSelectedPatient(null)
      setSearchText('')
      return
    }
    if (patient) return
    let cancelled = false
    setLoading(true)
    setError(false)
    dispatch(fetchAllPatients())
      .unwrap()
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [show, patient, dispatch])

  const handleSearchChange = (value: string): void => {
    setSearchText(value)
    const match = patientByLabel.get(value)
    if (match) setSelectedPatient(match)
  }

  const regenerate = async (): Promise<void> => {
    if (!otp || regenerating) return
    setRegenerating(true)
    try {
      const result = await createPatientFormsOtp({
        patient_id: otp.patient.patient_id,
        recipient: 'kiosk',
      })
      if (!result.code) throw new Error('Missing OTP')
      setOtp({ ...otp, code: result.code })
    } catch {
      showToast(
        createFailedToast(
          'Error',
          'Failed to regenerate the invitation code. Please try again.',
        ),
      )
    } finally {
      setRegenerating(false)
    }
  }

  return (
    <>
      <Modal
        open={show && !target}
        onClose={onHide}
        size="sm"
        title="Send forms"
        className="send-patient-forms-modal"
        showFooter={false}
      >
        {loading ? (
          <p className="send-patient-forms-modal__status">Loading patients…</p>
        ) : error ? (
          <p className="send-patient-forms-modal__status" role="alert">
            Failed to load patients. Close and try again.
          </p>
        ) : (
          <Combobox
            label="Patient"
            placeholder="Search for a patient"
            options={patientOptions}
            value={searchText}
            onChange={handleSearchChange}
            allowCustom={false}
            className="send-patient-forms-modal__search"
          />
        )}
      </Modal>
      {show && target && (
        <AssignFormsModal
          show={show}
          onHide={onHide}
          variant="direct"
          patientId={target.patient_id}
          publicPatientId={target.public_id}
          patientName={`${target.first_name} ${target.last_name}`.trim()}
          patientMobile={target.cellphone}
          onFormsAssigned={onFormsAssigned}
          onOTPShow={(_name, code) => setOtp({ patient: target, code })}
        />
      )}
      {otp && (
        <PatientOTPModal
          show
          onHide={() => setOtp(null)}
          patientName={`${otp.patient.first_name} ${otp.patient.last_name}`.trim()}
          otp={otp.code}
          patientId={otp.patient.patient_id}
          onRegenerate={() => {
            void regenerate()
          }}
        />
      )}
    </>
  )
}

export default SendPatientFormsModal
